export type WidgetType = "changelog" | "blog" | "kb" | "banner";

/** Where a banner widget sits: stuck to the top of the viewport, or in flow. */
export type BannerPosition = "top" | "inline";

export interface ShipstarInitOptions {
  apiKey: string;
  container: HTMLElement | string;
  type?: WidgetType;
  apiBaseUrl?: string;
  theme?: "light" | "dark" | "auto";
  locale?: string;
  maxItems?: number;
  branding?: boolean;
  subscribe?: boolean;
  search?: boolean;
  initialArticle?: string;
  onNavigate?: (slug: string) => void;
  /** Banner only: "top" (default) sticks to the top of the viewport, "inline" renders in flow */
  position?: BannerPosition;
  /** Banner only: show a dismiss button; a dismissed banner stays hidden until a new one is published */
  dismissible?: boolean;
  debug?: boolean;
  onLoad?: (data: WidgetData) => void;
  onError?: (error: Error) => void;
}

export type WidgetData =
  | ChangelogResponse
  | BlogPostResponse
  | BlogPostResponse[]
  | KbArticlesResponse
  | BannerResponse
  | null;

export interface ShipstarConfig {
  apiKey: string;
  type: WidgetType;
  apiBaseUrl: string;
  containerId: string | null;
  theme: "light" | "dark" | "auto";
  locale: string;
  maxItems: number;
  branding: boolean;
  subscribe: boolean;
  search: boolean;
  initialArticle: string | null;
  onNavigate: ((slug: string) => void) | null;
  position: BannerPosition;
  dismissible: boolean;
  debug: boolean;
  onLoad: ((data: WidgetData) => void) | null;
  onError: ((error: Error) => void) | null;
}

export interface ShipstarGlobal {
  __loaded: boolean;
  init: (options: ShipstarInitOptions) => void;
  destroy: (container: HTMLElement | string) => void;
  instances: Map<HTMLElement, { shadowRoot: ShadowRoot; config: ShipstarConfig }>;
}

declare global {
  interface Window {
    Shipstar?: ShipstarGlobal;
  }
}

export interface ChangelogEntry {
  id: string;
  category: "new" | "improved" | "fixed" | "breaking";
  title: string;
  description: string;
  date: string;
}

export interface ChangelogResponse {
  period_start: string;
  period_end: string;
  headline: string;
  entries: ChangelogEntry[];
  slug?: string;
}

export interface BlogPostResponse {
  slug: string;
  content: string;
  category: string | null;
  period_start: string | null;
  period_end: string | null;
  published_at: string | null;
  created_at: string | null;
}

export interface KbArticle {
  id: string;
  category?: string;
  title: string;
  content: string;
}

export interface KbArticlesResponse {
  slug: string;
  articles: KbArticle[];
  published_at: string | null;
  created_at: string | null;
}

export interface BannerResponse {
  slug: string;
  /** Short tag before the headline ("New" by default); empty means none */
  badge?: string;
  headline: string;
  body: string;
  cta_label: string;
  link_url: string | null;
  period_start: string | null;
  period_end: string | null;
  published_at: string | null;
  expires_at: string | null;
  commit_count?: number | null;
}

export class ShipstarError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ShipstarError";
  }
}

export class AuthError extends ShipstarError {
  constructor() {
    super("[Shipstar] Invalid or missing API key.");
    this.name = "AuthError";
  }
}

export class NotFoundError extends ShipstarError {
  constructor() {
    super("[Shipstar] Request failed with status 404");
    this.name = "NotFoundError";
  }
}

export class RateLimitError extends ShipstarError {
  constructor() {
    super("[Shipstar] Rate limit exceeded. Please try again later.");
    this.name = "RateLimitError";
  }
}
